import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { Empty, Button, Card, Row, Col } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { editTemplateSignersInfo } from '~/states/modules/editTemplate'
import styled from 'styled-components'

import Party from './components/party'
import Message from './components/Message'
import Observers from './components/Observers'
import ICPSigning from './components/ICPSigning'
import SigningOrder from './components/SigningOrder'

const StyledCard = styled(Card)`
	margin-bottom: 1.5rem;

	@media (max-width: 768px) {
		margin-bottom: 1rem;
	}
`

const Signers = () => {
	const { signers, message, responsible_group, icp_signing, signing_order } =
		useSelector(({ editTemplate }) => editTemplate.data.signers)

	const dispatch = useDispatch()

	const handleAddParty = () => {
		const newParty = {
			title: '',
			signers: [
				{
					title: '',
					anchor: [
						{
							anchor_string: '',
							anchor_x_offset: 0.0,
							anchor_y_offset: 0.0,
						},
					],
					fields: [
						{
							type: 'text',
							label: '',
							value: 'Nome',
							variable: '',
						},
						{
							type: 'email',
							label: '',
							value: 'Email',
							variable: '',
						},
					],
					status: '',
					signing_date: '',
				},
			],
		}
		const updatedSigningParties = {
			signers: [...signers, newParty],
			message,
			responsible_group,
			icp_signing,
			signing_order,
		}
		dispatch(editTemplateSignersInfo({ updatedSigningParties }))
	}

	const updateParty = (updatedParty, partyIndex) => {
		const updatedSigningParties = {
			signers: [
				...signers.map((p, index) => (index === partyIndex ? updatedParty : p)),
			],
			message,
			responsible_group,
			icp_signing,
			signing_order,
		}
		dispatch(editTemplateSignersInfo({ updatedSigningParties }))
	}

	const handleRemoveParty = (partyIndex) => {
		const updatedSigningParties = {
			signers: [...signers.filter((_, index) => index !== partyIndex)],
			message,
			responsible_group,
			icp_signing,
			signing_order,
		}
		dispatch(editTemplateSignersInfo({ updatedSigningParties }))
	}

	const handleMessageChange = (e) => {
		const updatedSigningParties = {
			signers,
			message: e.target.value,
			responsible_group,
			icp_signing,
			signing_order,
		}
		dispatch(editTemplateSignersInfo({ updatedSigningParties }))
	}

	const handleAddObserver = () => {
		const newObserver = {
			email: '',
			name: '',
		}
		const updatedSigningParties = {
			signers,
			message,
			responsible_group: [...responsible_group, newObserver],
			icp_signing,
			signing_order,
		}
		dispatch(editTemplateSignersInfo({ updatedSigningParties }))
	}

	const handleUpdateObserver = (index, field, value) => {
		const updatedObservers = [...responsible_group]
		updatedObservers[index] = {
			...updatedObservers[index],
			[field]: value,
		}
		const updatedSigningParties = {
			signers,
			message,
			responsible_group: updatedObservers,
			icp_signing,
			signing_order,
		}
		dispatch(editTemplateSignersInfo({ updatedSigningParties }))
	}

	const handleRemoveObserver = (index) => {
		const updatedSigningParties = {
			signers,
			message,
			responsible_group: [...responsible_group.filter((_, i) => i !== index)],
			icp_signing,
			signing_order,
		}
		dispatch(editTemplateSignersInfo({ updatedSigningParties }))
	}

	const handleICPSigningChange = (checked) => {
		const updatedSigningParties = {
			signers,
			message,
			responsible_group,
			icp_signing: checked,
			signing_order,
		}
		dispatch(editTemplateSignersInfo({ updatedSigningParties }))
	}

	const handleSigningOrderChange = (checked) => {
		const updatedSigningParties = {
			signers,
			message,
			responsible_group,
			icp_signing,
			signing_order: checked,
		}
		dispatch(editTemplateSignersInfo({ updatedSigningParties }))
	}

	return (
		<Row gutter={[24, 24]}>
			<Col xs={24} md={14}>
				<StyledCard title="Partes Signatárias">
					{!signers.length ? (
						<Empty
							description="Sem Partes"
							style={{ marginBottom: '1.5rem' }}
						/>
					) : (
						signers.map((party, index) => (
							<Party
								key={index}
								party={party}
								partyIndex={index}
								updateParty={updateParty}
								removeParty={() => handleRemoveParty(index)}
							/>
						))
					)}
					<Button
						block
						icon={<PlusOutlined />}
						size="large"
						type="dashed"
						onClick={() => handleAddParty()}
						style={{ height: '4rem', marginBottom: '1rem' }}>
						Nova Parte
					</Button>
				</StyledCard>
			</Col>
			<Col xs={24} md={10}>
				<Message message={message} onMessageChange={handleMessageChange} />
				<Observers
					observers={responsible_group}
					onAddObserver={handleAddObserver}
					onUpdateObserver={handleUpdateObserver}
					onRemoveObserver={handleRemoveObserver}
				/>
				<ICPSigning
					icpSigning={icp_signing}
					onICPSigningChange={handleICPSigningChange}
				/>
				<SigningOrder
					signingOrder={signing_order}
					onSigningOrderChange={handleSigningOrderChange}
				/>
			</Col>
		</Row>
	)
}

export default Signers

Signers.propTypes = {
	signingParties: PropTypes.shape({
		message: PropTypes.string,
		responsible_group: PropTypes.arrayOf(
			PropTypes.shape({
				email: PropTypes.string,
				name: PropTypes.string,
			})
		),
		signers: PropTypes.arrayOf(
			PropTypes.shape({
				anchor: PropTypes.arrayOf(
					PropTypes.shape({
						anchor_string: PropTypes.string,
						anchor_x_offset: PropTypes.number,
						anchor_y_offset: PropTypes.number,
					})
				),
				fields: PropTypes.arrayOf(
					PropTypes.shape({
						label: PropTypes.string,
						type: PropTypes.string,
						value: PropTypes.string,
						variable: PropTypes.string,
					})
				),
				party: PropTypes.string,
				signing_date: PropTypes.string,
				status: PropTypes.string,
				title: PropTypes.string,
			})
		),
		icp_signing: PropTypes.bool,
	}),
}
